import type { ReactNode } from 'react';
import Box from '@mui/material/Box';

// A tiny CSS-grid based container/item pair used instead of MUI's <Grid> — MUI v6 ships the old
// item/xs API on @mui/material/Grid (not the size={{...}} Grid2 API from later MUI versions), so
// this sidesteps the version-prop mismatch across every screen rather than getting it wrong once
// per file.

interface GridContainerProps {
  columns?: number;
  spacing?: number;
  children: ReactNode;
}

export function GridContainer({ columns = 12, spacing = 2, children }: GridContainerProps) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: spacing }}>{children}</Box>
  );
}

interface GridItemProps {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  children: ReactNode;
}

export function GridItem({ xs = 12, sm, md, lg, children }: GridItemProps) {
  // An item that names NO breakpoint was written as a fixed multi-column layout with no phone in
  // mind — e.g. `xs={1}` inside `columns={2}` puts two text fields side by side in ~160px each on a
  // 360px screen. Those collapse to one-per-row below sm ('1 / -1' spans the full row whatever the
  // container's column count is), with the authored span taking over from sm up.
  //
  // An item that DOES name a breakpoint was authored breakpoint-aware, so its xs is a deliberate
  // choice (a 2-up tile row that should stay 2-up on a phone, say) and is left exactly as written.
  const authoredResponsively = sm !== undefined || md !== undefined || lg !== undefined;

  return (
    <Box
      sx={{
        gridColumn: authoredResponsively
          ? {
              xs: `span ${xs}`,
              ...(sm ? { sm: `span ${sm}` } : {}),
              ...(md ? { md: `span ${md}` } : {}),
              ...(lg ? { lg: `span ${lg}` } : {}),
            }
          : { xs: '1 / -1', sm: `span ${xs}` },
      }}
    >
      {children}
    </Box>
  );
}
